import { memo, useEffect, useState } from "react";
import { Box, Modal, IconButton } from "@mui/material";

import Close from "@zenith/assets/icons/Close";
import ChevronDown from "@zenith/assets/icons/ChevronDown";
import { ButtonComponent, SelectComponent } from "@zenith/components";
import { OutletListDTO } from "@zenith/services/user";
import { ROLE_SELECT_ITEMS, TeamMemberRole } from "../../data/roles";
import SelectOutletsPanel from "../SelectOutletsPanel/select-outlets-panel";

export interface EditTeamMemberModalProps {
  showModal: boolean;
  role: TeamMemberRole;
  outletIds: string[] | "ALL";
  outlets: OutletListDTO[];
  onClose: () => void;
  onViewBreakdown: (role: TeamMemberRole) => void;
  onUpdate: (role: TeamMemberRole, outletIds: string[] | "ALL") => void;
}

const EditTeamMemberModal = memo(function EditTeamMemberModal(
  props: EditTeamMemberModalProps
) {
  const {
    showModal,
    role: initialRole,
    outletIds: initialOutletIds,
    outlets,
    onClose,
    onViewBreakdown,
    onUpdate,
  } = props;

  const [role, setRole] = useState(initialRole);
  const [outletIds, setOutletIds] = useState<string[]>(
    initialOutletIds === "ALL" ? outlets.map((o) => o.id ?? "") : initialOutletIds
  );
  const [showOutletsPanel, setShowOutletsPanel] = useState(false);

  useEffect(() => {
    if (!showModal) return;
    setRole(initialRole);
    setOutletIds(
      initialOutletIds === "ALL"
        ? outlets.map((o) => o.id ?? "")
        : initialOutletIds
    );
  }, [showModal, initialRole, initialOutletIds, outlets]);

  const isAdmin = role === TeamMemberRole.ADMIN;
  const allSelected = outletIds.length === outlets.length;
  const outletsLabel = allSelected
    ? `All outlet (${outlets.length})`
    : outletIds.length > 0
    ? `outlet (${outletIds.length})`
    : "Assign Outlets";

  const removeOutlet = (id: string) =>
    setOutletIds((prev) => prev.filter((o) => o !== id));

  const hasChanged =
    role !== initialRole ||
    (initialOutletIds === "ALL"
      ? !allSelected
      : JSON.stringify([...outletIds].sort()) !==
        JSON.stringify([...initialOutletIds].sort()));

  const handleUpdate = () => {
    onUpdate(role, allSelected ? "ALL" : outletIds);
  };

  return (
    <Modal open={showModal} onClose={onClose} className="overflow-visible">
      <Box className="absolute left-1/2 top-1/2 z-0 flex max-h-[90vh] w-[95%] max-w-[668px] -translate-x-1/2 -translate-y-1/2 transform flex-col overflow-y-auto rounded-[20px] bg-white px-8 py-6 outline-none">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-2xl font-medium text-neutral-900">
            Edit Team Member
          </h2>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </div>

        <div className="mb-1">
          <SelectComponent
            id="edit-team-member-role"
            labelId="edit-team-member-role-label"
            label="Role"
            className="w-full"
            items={ROLE_SELECT_ITEMS}
            // Without this the menu opens behind the modal panel and the
            // options cannot be clicked at all.
            listboxZIndex={1300}
            value={role}
            onChange={(_, value) => {
              const next = (value as TeamMemberRole) ?? role;
              const wasAdmin = isAdmin;
              setRole(next);
              if (next === TeamMemberRole.ADMIN) {
                setOutletIds(outlets.map((o) => o.id ?? ""));
              } else if (wasAdmin) {
                // Coming off Admin's "every outlet" default - a non-admin
                // role was never meant to inherit that, so it starts blank
                // and has to be explicitly assigned. Switching between two
                // non-admin roles keeps whatever was already picked.
                setOutletIds([]);
              }
            }}
          />
        </div>
        <button
          type="button"
          onClick={() => onViewBreakdown(role)}
          className="mb-5 text-xs font-normal text-neutral-700 underline"
        >
          View role access breakdown
        </button>

        <div className="relative mb-3 w-full">
          <label className="label mb-1.5 block text-sm font-normal text-neutral-500">
            Outlets
          </label>
          <button
            type="button"
            disabled={isAdmin}
            onClick={() => setShowOutletsPanel((v) => !v)}
            className={`flex w-full items-center justify-between rounded-[10px] border border-neutral-300 px-4 py-[13px] text-left text-base ${
              isAdmin
                ? "cursor-not-allowed bg-neutral-100 text-neutral-500"
                : "text-neutral-600"
            }`}
          >
            {outletsLabel}
            <ChevronDown />
          </button>
          {showOutletsPanel && !isAdmin && (
            <SelectOutletsPanel
              outlets={outlets}
              selectedIds={outletIds}
              onChange={setOutletIds}
              onClose={() => setShowOutletsPanel(false)}
            />
          )}
        </div>

        {!isAdmin && !allSelected && outletIds.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2">
            {outletIds.map((id) => {
              const outlet = outlets.find((o) => o.id === id);
              if (!outlet) return null;
              return (
                <span
                  key={id}
                  className="flex items-center gap-1 rounded-full bg-neutral-75 px-3 py-1 text-xs text-neutral-700"
                >
                  {outlet.name}
                  <button
                    type="button"
                    onClick={() => removeOutlet(id)}
                    className="text-neutral-400"
                  >
                    ×
                  </button>
                </span>
              );
            })}
          </div>
        )}

        <div className="mt-2 flex w-full gap-4">
          <ButtonComponent
            id="edit-team-member-cancel"
            type="secondary"
            size="large"
            label="Cancel"
            className="flex-1"
            onClick={onClose}
          />
          <ButtonComponent
            id="edit-team-member-update"
            type="primary"
            size="large"
            label="Update"
            className="flex-1"
            disabled={!hasChanged || (!isAdmin && outletIds.length === 0)}
            onClick={handleUpdate}
          />
        </div>
      </Box>
    </Modal>
  );
});

export default EditTeamMemberModal;
